import {
  CaseGetRecentDuplicateCaseCaseTypeId,
  CaseStatusEnum,
  CaseTypeEnum,
  RecentDuplicateCaseResult,
  TyreTypeEnum,
} from 'api/model';
import { useCaseGetRecentDuplicateCaseHook } from 'api/case/case';
import Input, { CombineInputs } from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import KeyValueList, { KeyListRow } from 'components/KeyValueList';
import useTranslations from 'contexts/basicData/useTranslations';
import {
  CaseDataField,
  caseDataFormToCaseModel,
  caseTypeDefaultsToCancellationSmsToBooker,
} from '../DetailedCaseFormProvider/useCaseDataForm';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import {
  useDetailedCaseDataForm,
  useDetailedCaseFormControl,
} from '../DetailedCaseFormProvider';
import FormValidations from '../FormValidations';
import CompanyPicker from 'components/CompanyPicker';
import { arraySpreadIf } from 'utils/spreading';
import Checkbox from 'components/inputs/Checkbox';
import useMe from 'contexts/authentication/useMe';
import Roles from 'constants/Roles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAssignmentWarnings from 'hooks/useAssignmentWarnings';
import { useDebounceCallback } from 'usehooks-ts';
import { useApiCallFn } from 'hooks/useApiCallFn';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import useIsExcludedFromFullöDayPlanning from 'hooks/useHasTestAccessToPlaceholderCases';
import useHasTestAccessToCarTransportTrucks from 'hooks/useHasTestAccessToCarTransportTrucks';
import CarTransportTruckPicker from 'components/CarTransportTruckPicker';
import Routes from 'constants/Routes';
import TextButton from 'components/inputs/TextButton';
import { isVehicleLessCaseType } from '../CaseAssignments/utils';

const Wrapper = styled.div``;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
`;

const BigLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 50px;
  font-weight: bold;
  ${({ theme }) => css`
    color: ${theme.colors.foreground.tertiary};
  `}
  // only upper-case
  text-transform: uppercase;
`;

const Warning = styled.div`
  ${({ theme }) => css`
    color: ${theme.colors.background.negative};
  `}
`;
interface Props {
  className?: string;
}

const CaseDataForm: FC<Props> = ({ className }) => {
  const assignmentWarnings = useAssignmentWarnings();
  const { isNewCase } = useDetailedCaseFormControl();
  const hasAccessToPlaceholderBookings = useIsExcludedFromFullöDayPlanning();
  const hasAccessToCarTransportTruck = useHasTestAccessToCarTransportTrucks();

  const { input, select, onChange, state, validation } =
    useDetailedCaseDataForm();
  const { caseTypes, caseStatuses, tyreTypes, zipCodeAreas } =
    useTranslations();

  const selectedCaseStatus = Number(state[CaseDataField.caseStatusID]);
  const selectableCaseStatuses: CaseStatusEnum[] = Object.values(
    CaseStatusEnum
  ).filter(
    (v) =>
      typeof v === 'number' &&
      (v === selectedCaseStatus || v !== CaseStatusEnum.Pending)
  ) as CaseStatusEnum[];

  const currentCaseWarnings = useMemo(() => {
    const caseModel = caseDataFormToCaseModel(undefined, state, zipCodeAreas);

    const warnings = assignmentWarnings.getCaseWarnings(caseModel);

    return warnings;
  }, [assignmentWarnings, state, zipCodeAreas]);

  const me = useMe();
  const isExternalAdmin = me?.roles.includes(Roles.ExternalAdmin);
  const canEdit = useMemo(() => {
    // return false if the user is an external admin and the case has been closed
    if (
      isExternalAdmin &&
      Number(state[CaseDataField.caseStatusID]) === CaseStatusEnum.Closed
    ) {
      return false;
    }
    return true;
  }, [isExternalAdmin, state]);

  const selectableCaseTypes: CaseTypeEnum[] = useMemo(() => {
    const allowedCaseTypesForAnnonsAndNotAdmin: number[] = [
      CaseTypeEnum.InternalDelivery,
      CaseTypeEnum.TestDrive,
      CaseTypeEnum.RentedStaff,  // Replaces "Extra" in frontend
      CaseTypeEnum.Samakning,
      CaseTypeEnum.Verkstadsbilar,
      CaseTypeEnum.FacilityToFacility,
      CaseTypeEnum.FacilityTransfer,
      CaseTypeEnum.RMOther,
      CaseTypeEnum.Avyttringar,
      CaseTypeEnum.Garantiarenden,
      CaseTypeEnum.AterTillLogistik,
      CaseTypeEnum.Dackkorning,
      CaseTypeEnum.Swamp,
    ];

    if (me?.roles.includes(Roles.Annons) && !me?.roles.includes(Roles.Admin)) {
      return Object.values(CaseTypeEnum).filter(
        (v) =>
          typeof v === 'number' &&
          allowedCaseTypesForAnnonsAndNotAdmin.includes(v)
      ) as CaseTypeEnum[];
    }

    const result = Object.values(CaseTypeEnum).filter(
      (v) =>
        typeof v === 'number' &&
        (v !== CaseTypeEnum.CarTransportTruck ||
          hasAccessToCarTransportTruck) && // Exclude CarTransportTruck until release
        v !== CaseTypeEnum.Extra && // Extra replaced by RentedStaff in UI
        (v !== CaseTypeEnum.Placeholder || hasAccessToPlaceholderBookings) // Exclude Placeholder if user does not have access to it while we are testing the feature in production // Jonas Frid 2024-11-26
    ) as CaseTypeEnum[];

    return result;
  }, [hasAccessToCarTransportTruck, hasAccessToPlaceholderBookings, me?.roles]);

  const selectableTyreTypes: TyreTypeEnum[] = useMemo(() => {
    return Object.values(TyreTypeEnum).filter(
      (v) => typeof v === 'number'
    ) as TyreTypeEnum[];
  }, []);

  const currentCaseType = Number(
    state[CaseDataField.caseTypeID]
  ) as CaseTypeEnum;
  const registrationNumber = state[CaseDataField.registrationNumber];

  const [recentDuplicate, setRecentDuplicate] =
    useState<RecentDuplicateCaseResult | null>(null);
  const getRecentDuplicateCase = useCaseGetRecentDuplicateCaseHook();
  const { run: runRecentDuplicate } = useApiCallFn(
    (reg: string, caseType: number) =>
      getRecentDuplicateCase({
        registrationNumber: reg,
        caseTypeId: caseType as CaseGetRecentDuplicateCaseCaseTypeId,
      })
  );

  const fetchRecentDuplicate = useCallback(
    async (reg: string, caseType: number) => {
      const normalized = reg.trim().toUpperCase();
      if (normalized.length < 6 || !caseType) {
        setRecentDuplicate(null);
        return;
      }
      const [response] = await runRecentDuplicate(normalized, caseType);
      setRecentDuplicate(response ?? null);
    },
    [runRecentDuplicate]
  );

  const debouncedFetchRecentDuplicate = useDebounceCallback(
    fetchRecentDuplicate,
    600
  );

  useEffect(() => {
    const reg = (registrationNumber ?? '').replace(/\s+/g, ''); // remove internal spaces
    if (reg.length >= 6 && currentCaseType) {
      debouncedFetchRecentDuplicate(reg, currentCaseType);
    } else {
      setRecentDuplicate(null);
    }
  }, [debouncedFetchRecentDuplicate, registrationNumber, currentCaseType]);
  useEffect(() => {
    if (
      currentCaseType === CaseTypeEnum.InternalDelivery ||
      currentCaseType === CaseTypeEnum.TestDrive
    ) {
      onChange(CaseDataField.customerName, '-');
    } else if (state[CaseDataField.customerName] === '-') {
      onChange(CaseDataField.customerName, '');
    }

    if (
      (
      [
        CaseTypeEnum.HomeDelivery,
        CaseTypeEnum.Purchase,
        CaseTypeEnum.Auction,
        ] as number[]
      ).includes(currentCaseType) &&
      state[CaseDataField.sendCustomerSurvey] !== 'false'
    ) {
      onChange(CaseDataField.sendCustomerSurvey, 'true');
    } else {
      onChange(CaseDataField.sendCustomerSurvey, 'false');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCaseType]);

  // SMS till säljaren vid avbokning: följ den nya ärendetypens standard när TL byter typ —
  // men bara om rutan står på den gamla typens standard. Har TL själv ändrat den ligger valet
  // kvar. Bara vid faktiskt typbyte; vid öppning ska ärendets sparade val stå kvar.
  const previousCaseTypeRef = useRef(currentCaseType);
  useEffect(() => {
    const previousCaseType = previousCaseTypeRef.current;
    if (previousCaseType === currentCaseType) {
      return;
    }
    previousCaseTypeRef.current = currentCaseType;
    const previousDefault = caseTypeDefaultsToCancellationSmsToBooker(
      previousCaseType
    )
      ? 'true'
      : 'false';
    if (state[CaseDataField.sendCancellationSmsToBooker] !== previousDefault) {
      return;
    }
    onChange(
      CaseDataField.sendCancellationSmsToBooker,
      caseTypeDefaultsToCancellationSmsToBooker(currentCaseType)
        ? 'true'
        : 'false'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCaseType]);

  const canEditField = useCallback(
    (field: CaseDataField) => {
      if (!canEdit) {
        return false;
      }

      if (currentCaseType === CaseTypeEnum.TestDrive) {
        // specific treatment for TestDrives (to enable Riddermark to edit regardless if also ExternalAdmin)
        const allowedFields = [
          CaseDataField.caseStatusID,
          CaseDataField.companyID,
          CaseDataField.bookerName,
          CaseDataField.bookerPhoneNumber,
          CaseDataField.customerName,
          CaseDataField.customerPhoneNumber,
          CaseDataField.registrationNumber,
          CaseDataField.vehicleGearBox,
          CaseDataField.vehicleMileage,
          CaseDataField.vehicleYearModel,
          CaseDataField.vehicleBrand,
          CaseDataField.vehicleModel,
          CaseDataField.vehicleBody,
          CaseDataField.vehicleFuel,
          CaseDataField.isTrailer,
          CaseDataField.isTruck,
        ];

        return allowedFields.includes(field);
      }

      if (!isExternalAdmin) {
        return true;
      }

      // specific treatment for external admins
      const allowedFields = [
        CaseDataField.bookerName,
        CaseDataField.bookerPhoneNumber,
        CaseDataField.customerName,
        CaseDataField.customerPhoneNumber,
        CaseDataField.vehicleMileage,
        CaseDataField.vehicleYearModel,
      ];

      return allowedFields.includes(field);
    },
    [canEdit, currentCaseType, isExternalAdmin]
  );

  const mkInput = useCallback(
    (
      name: CaseDataField,
      label: string,
      type?: React.HTMLInputTypeAttribute,
      exclude?: () => boolean,
      disabled?: boolean
    ): KeyListRow & { exclude?: () => boolean } => ({
      key: label,
      exclude,
      value: (
        <Input
          onChange={input(name)}
          small
          type={type}
          value={state[name]}
          disabled={disabled}
        />
      ),
    }),
    [input, state]
  );

  const hideIfCarTransportTruck = useCallback(
    () => currentCaseType === CaseTypeEnum.CarTransportTruck,
    [currentCaseType]
  );

  // Uthyrd personal (RentedStaff), Samåkning och Däckkörning handlar inte om ett fordon. Vi
  // döljer därför fordons- och aviseringsfält för dessa ärendetyper (utöver det som redan döljs
  // för lastbilstransport). För RentedStaff och Däckkörning behålls Kund/Kund tel — för
  // RentedStaff anges där företaget dit fälttestaren hyrs ut — men för Samåkning döljs de.
  const hideIfCarTransportTruckOrStaffRental = useCallback(
    () => hideIfCarTransportTruck() || isVehicleLessCaseType(currentCaseType),
    [hideIfCarTransportTruck, currentCaseType]
  );

  // Samåkning är inte kundvänd och saknar kund — dölj Kund/Kund tel (till skillnad från
  // Uthyrd personal som visar dem).
  const hideIfCarTransportTruckOrSamakning = useCallback(
    () =>
      hideIfCarTransportTruck() || currentCaseType === CaseTypeEnum.Samakning,
    [hideIfCarTransportTruck, currentCaseType]
  );

  const firstColumn = useMemo(() => {
    const result: (KeyListRow & {
      exclude?: () => boolean;
    })[] = [
      {
        key: 'Typ',
        value: (
          <Select
            onChange={select(CaseDataField.caseTypeID)}
            small
            disabled={
              (me != null && me.roles.includes(Roles.Riddermark)) ||
              !canEditField(CaseDataField.caseTypeID)
            }
            value={state[CaseDataField.caseTypeID]}
          >
            {selectableCaseTypes.map((type) => (
              <option key={type} value={type}>
                {caseTypes[type]}
              </option>
            ))}
          </Select>
        ),
      },
      {
        key: 'Status',
        value: (
          <Select
            onChange={select(CaseDataField.caseStatusID)}
            small
            value={state[CaseDataField.caseStatusID]}
            disabled={!canEditField(CaseDataField.caseStatusID)}
          >
            {selectableCaseStatuses.map((type) => (
              <option key={type} value={type}>
                {caseStatuses[type]}
              </option>
            ))}
          </Select>
        ),
      },
      mkInput(
        CaseDataField.closedDate,
        'Stängdes',
        'datetime-local',
        () =>
          Number(state[CaseDataField.caseStatusID]) !== CaseStatusEnum.Closed ||
          !state[CaseDataField.closedDate],
        !canEditField(CaseDataField.closedDate)
      ),
      mkInput(
        CaseDataField.completedDate,
        'Slutfört',
        'datetime-local',
        () =>
          Number(state[CaseDataField.caseStatusID]) !== CaseStatusEnum.Closed ||
          !state[CaseDataField.completedDate],
        !canEditField(CaseDataField.completedDate)
      ),
      {
        key: 'Företag',
        value: (
          <CompanyPicker
            disabled={!canEditField(CaseDataField.companyID)}
            onCompanySelected={(company) => {
              onChange(
                CaseDataField.companyID,
                company ? String(company.companyID) : ''
              );
            }}
            filter={(company) => {
              if (
                state[CaseDataField.caseTypeID] ===
                String(CaseTypeEnum.InternalDelivery)
              ) {
                return (
                  company.showInInternalDeliveryOverview === true ||
                  company.isLogisticsCenter === true
                );
              }
              return true;
            }}
            selectedCompanyId={
              state[CaseDataField.companyID] === ''
                ? null
                : Number(state[CaseDataField.companyID])
            }
          />
        ),
      },
      {
        key: 'Lastbil (Zäkra)',
        value: (
          <CarTransportTruckPicker
            disabled={!canEditField(CaseDataField.carTransportTruckID)}
            onCarTransportTruckSelected={(carTransportTruck) => {
              onChange(
                CaseDataField.carTransportTruckID,
                carTransportTruck ? String(carTransportTruck.id) : ''
              );
            }}
            selectedCarTransportTruckId={
              state[CaseDataField.carTransportTruckID] === ''
                ? null
                : Number(state[CaseDataField.carTransportTruckID])
            }
          />
        ),
        exclude: () => !hideIfCarTransportTruck(),
      },
      mkInput(
        CaseDataField.bookerName,
        'Bokare',
        undefined,
        hideIfCarTransportTruck,
        !canEditField(CaseDataField.bookerName)
      ),
      mkInput(
        CaseDataField.bookerPhoneNumber,
        'Bokare tel',
        undefined,
        hideIfCarTransportTruck,
        !canEditField(CaseDataField.bookerPhoneNumber)
      ),
    ];

    return result.filter((f) => (f.exclude ? f.exclude() !== true : true));
  }, [
    canEditField,
    caseStatuses,
    caseTypes,
    hideIfCarTransportTruck,
    me,
    mkInput,
    onChange,
    select,
    selectableCaseStatuses,
    selectableCaseTypes,
    state,
  ]);

  const secondColumn = useMemo(() => {
    const result = [
      // Kund/Kund tel visas även för Uthyrd personal — där används de för att
      // ange företaget dit fälttestaren hyrs ut. För Samåkning döljs de däremot.
      mkInput(
        CaseDataField.customerName,
        'Kund',
        undefined,
        hideIfCarTransportTruckOrSamakning,
        !canEditField(CaseDataField.customerName)
      ),
      mkInput(
        CaseDataField.customerPhoneNumber,
        'Kund tel',
        undefined,
        hideIfCarTransportTruckOrSamakning,
        !canEditField(CaseDataField.customerPhoneNumber)
      ),
      {
        field: CaseDataField.registrationNumber,
        key: 'Regnr',
        exclude: hideIfCarTransportTruckOrStaffRental,
        value: (
          <CombineInputs>
            <Input
              disabled={!canEditField(CaseDataField.registrationNumber)}
              onChange={input(CaseDataField.registrationNumber)}
              small
              value={state[CaseDataField.registrationNumber]}
              width={90}
            />
            <Select
              disabled={!canEditField(CaseDataField.vehicleGearBox)}
              onChange={select(CaseDataField.vehicleGearBox)}
              small
              value={state[CaseDataField.vehicleGearBox]}
              width={90}
            >
              {/* <option value="">Okänt</option> */}
              <option value="true">Manuell</option>
              <option value="false">Automat</option>
            </Select>
          </CombineInputs>
        ),
      },
      mkInput(
        CaseDataField.vehicleMileage,
        'Miltal',
        'number',
        hideIfCarTransportTruckOrStaffRental,
        !canEditField(CaseDataField.vehicleMileage)
      ),
      mkInput(
        CaseDataField.rideOrderID,
        'RideOrderID',
        undefined,
        hideIfCarTransportTruckOrStaffRental,
        (me != null && me.roles.includes(Roles.Riddermark)) ||
          !canEditField(CaseDataField.rideOrderID)
      ),
    ];
    return result.filter((f) => (f.exclude ? f.exclude() !== true : true));
  }, [
    canEditField,
    hideIfCarTransportTruckOrStaffRental,
    hideIfCarTransportTruckOrSamakning,
    input,
    me,
    mkInput,
    select,
    state,
  ]);

  const fourthColumn = useMemo(() => {
    const result = [
      mkInput(
        CaseDataField.vehicleYearModel,
        'Årsmodell',
        'number',
        hideIfCarTransportTruckOrStaffRental,
        !canEditField(CaseDataField.vehicleYearModel)
      ),
      mkInput(
        CaseDataField.vehicleBrand,
        'Märke',
        undefined,
        hideIfCarTransportTruckOrStaffRental,
        !canEditField(CaseDataField.vehicleBrand)
      ),
      mkInput(
        CaseDataField.vehicleModel,
        'Modell',
        undefined,
        hideIfCarTransportTruckOrStaffRental,
        !canEditField(CaseDataField.vehicleModel)
      ),
      mkInput(
        CaseDataField.vehicleBody,
        'Karosstyp',
        undefined,
        hideIfCarTransportTruckOrStaffRental,
        !canEditField(CaseDataField.vehicleBody)
      ),
      mkInput(
        CaseDataField.vehicleFuel,
        'Drivmedel',
        undefined,
        hideIfCarTransportTruckOrStaffRental,
        !canEditField(CaseDataField.vehicleFuel)
      ),
    ];
    return result.filter((f) => (f.exclude ? f.exclude() !== true : true));
  }, [canEditField, hideIfCarTransportTruckOrStaffRental, mkInput]);

  const thirdColumn = useMemo(() => {
    const result = [
      {
        // Elbil är ett obligatoriskt Ja/Nej-svar (inget default). Styr laddningsbufferten
        // på uppdragens estimerade tid samt Elbil-fakturaextran. Valideras i useCaseDataForm.
        key: 'Elbil',
        value: (
          <Select
            disabled={!canEditField(CaseDataField.isElectric)}
            onChange={select(CaseDataField.isElectric)}
            small
            value={state[CaseDataField.isElectric]}
          >
            <option value="">Välj Ja/Nej</option>
            <option value="true">Ja</option>
            <option value="false">Nej</option>
          </Select>
        ),
        exclude: () =>
          currentCaseType === CaseTypeEnum.Placeholder ||
          hideIfCarTransportTruckOrStaffRental(),
      },
      {
        key: 'BE-körkort (släp)',
        value: (
          <Checkbox
            disabled={!canEditField(CaseDataField.isTrailer)}
            checked={state[CaseDataField.isTrailer] === 'true'}
            onChange={(eve) =>
              onChange(
                CaseDataField.isTrailer,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
        exclude: hideIfCarTransportTruckOrStaffRental,
      },
      {
        key: 'C-körkort (lastbil)',
        value: (
          <Checkbox
            disabled={!canEditField(CaseDataField.isTruck)}
            checked={state[CaseDataField.isTruck] === 'true'}
            onChange={(eve) =>
              onChange(
                CaseDataField.isTruck,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
        exclude: hideIfCarTransportTruckOrStaffRental,
      },
      ...arraySpreadIf(
        Number(state[CaseDataField.caseTypeID]) ===
          CaseTypeEnum.InternalDelivery,
        {
          key: 'Däcktyp',
          value: (
            <Select
              disabled={!canEditField(CaseDataField.vehicleTyreType)}
              onChange={select(CaseDataField.vehicleTyreType)}
              small
              value={state[CaseDataField.vehicleTyreType]}
            >
              <option value={undefined}>Välj däcktyp</option>
              {selectableTyreTypes.map((type) => (
                <option key={type} value={type}>
                  {tyreTypes[type]}
                </option>
              ))}
            </Select>
          ),
          exclude: () =>
            hideIfCarTransportTruck() ||
            Number(state[CaseDataField.caseTypeID]) !==
              CaseTypeEnum.InternalDelivery,
        }
      ),
      {
        key: 'Skicka kundundersökning',
        value: (
          <span>
            <Checkbox
              disabled={
                !(
                  [
                  CaseTypeEnum.HomeDelivery,
                  CaseTypeEnum.Purchase,
                  CaseTypeEnum.Auction,
                  ] as number[]
                ).includes(currentCaseType) ||
                !canEditField(CaseDataField.sendCustomerSurvey)
              }
              checked={state[CaseDataField.sendCustomerSurvey] === 'true'}
              onChange={(eve) =>
                onChange(
                  CaseDataField.sendCustomerSurvey,
                  eve.currentTarget.checked ? 'true' : 'false'
                )
              }
            />
            {!(
              [
              CaseTypeEnum.HomeDelivery,
              CaseTypeEnum.Purchase,
              CaseTypeEnum.Auction,
              ] as number[]
            ).includes(currentCaseType) && (
              <span
                style={{
                  marginLeft: 5,
                }}
              >
                (endast för inköp, hemleverans och auktionsbilar)
              </span>
            )}
          </span>
        ),
        exclude: hideIfCarTransportTruckOrStaffRental,
      },
      {
        // Säljaren (bokaren) får ett SMS när TL avbokar en leverans. Förvalt för inköp,
        // hemleverans och inbyte; TL kan slå av eller på per ärende.
        key: 'SMS till säljare vid avbokning',
        value: (
          <Checkbox
            disabled={
              !canEditField(CaseDataField.sendCancellationSmsToBooker)
            }
            checked={
              state[CaseDataField.sendCancellationSmsToBooker] === 'true'
            }
            onChange={(eve) =>
              onChange(
                CaseDataField.sendCancellationSmsToBooker,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
        exclude: hideIfCarTransportTruckOrStaffRental,
      },
      {
        // VIKTIGT: markerar ärendet så det samlas i VIKTIGT-listan tills ett uppdrag planeras.
        // Ärendet behåller sin riktiga ärendetyp. Orthogonalt attribut — visas för alla typer
        // (utom lastbilstransport).
        key: 'Viktigt',
        value: (
          <Checkbox
            disabled={!canEditField(CaseDataField.isImportant)}
            checked={state[CaseDataField.isImportant] === 'true'}
            onChange={(eve) =>
              onChange(
                CaseDataField.isImportant,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
        exclude: hideIfCarTransportTruck,
      },
      // "Ska på lastbil" ligger per uppdrag (ben) — kryssrutan finns i uppdragsformuläret
      // (useCaseAssignmentFormRows), inte här i ärendehuvudet.
    ];
    return result.filter((f) => (f.exclude ? f.exclude() !== true : true));
  }, [
    canEditField,
    state,
    hideIfCarTransportTruck,
    hideIfCarTransportTruckOrStaffRental,
    select,
    selectableTyreTypes,
    currentCaseType,
    onChange,
    tyreTypes,
  ]);

  return (
    <Wrapper className={className}>
      <Row>
        {currentCaseType === CaseTypeEnum.Placeholder ? (
          <BigLabel>
            <FontAwesomeIcon icon={faWarning} />
            {caseTypes[currentCaseType]}
            <FontAwesomeIcon icon={faWarning} />
          </BigLabel>
        ) : (
          <>
            <KeyValueList colonKey keyWidth="90px" rows={firstColumn} />
            <KeyValueList colonKey keyWidth="90px" rows={secondColumn} />
            <KeyValueList colonKey keyWidth="90px" rows={fourthColumn} />
            <KeyValueList colonKey keyWidth="90px" rows={thirdColumn} />
          </>
        )}
      </Row>
      <FormValidations validations={validation} />
      {currentCaseWarnings.map((warning) => (
        <Warning key={warning.warning}>
          {warning.icon ?? <FontAwesomeIcon icon={faWarning} />}{' '}
          {warning.warning}
        </Warning>
      ))}
      {isNewCase && recentDuplicate && (
        <Warning>
          <FontAwesomeIcon icon={faWarning} /> OBS! Denna bil existerar redan i
          systemet:{' '}
          <TextButton
            onClick={() => {
              window.open(
                Routes.case.case.replace(':id', String(recentDuplicate.caseId))
              );
            }}
          >
            {recentDuplicate.registrationNumber} (#{recentDuplicate.caseId})
          </TextButton>
        </Warning>
      )}
    </Wrapper>
  );
};

export default CaseDataForm;
